import { GRID_SIZE, TOTAL_PIECES, SCORING, DIFFICULTIES, getPiecesCount } from './constants';

/**
 * Generate shuffled puzzle pieces
 * @param {string} imageUrl - The puzzle image URL
 * @param {string} difficulty - Difficulty level
 * @param {number} gridSize - Grid dimension (4 for mobile, 6 for desktop)
 */
export function generatePuzzlePieces(imageUrl, difficulty, gridSize = GRID_SIZE) {
  const pieces = [];
  const rotations = [0, 90, 180, 270];
  
  for (let row = 0; row < gridSize; row++) {
    for (let col = 0; col < gridSize; col++) {
      const id = row * gridSize + col;
      const rotation = DIFFICULTIES[difficulty].rotatesPieces 
        ? rotations[Math.floor(Math.random() * rotations.length)]
        : 0;
      
      pieces.push({
        id,
        correctRow: row,
        correctCol: col,
        currentRow: null,
        currentCol: null,
        rotation,
        isPlaced: false,
        isCorrect: false,
        imageUrl
      });
    }
  }
  
  // Shuffle the pieces
  return shuffleArray([...pieces]);
}

/**
 * Fisher-Yates shuffle algorithm
 */
export function shuffleArray(array) {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

/**
 * Check if a piece is in the correct position
 */
export function isPieceCorrect(piece, row, col, difficulty) {
  const positionCorrect = piece.correctRow === row && piece.correctCol === col;
  
  if (DIFFICULTIES[difficulty].rotatesPieces) {
    return positionCorrect && piece.rotation === 0;
  }
  
  return positionCorrect;
}

/**
 * Calculate the final score
 * @param {number} timeSeconds - Time taken in seconds
 * @param {number} moves - Number of moves
 * @param {string} difficulty - Difficulty level
 * @param {number} gridSize - Grid dimension (4 for mobile, 6 for desktop)
 */
export function calculateScore(timeSeconds, moves, difficulty, gridSize = GRID_SIZE) {
  const totalPieces = getPiecesCount(gridSize);
  
  // Base score from time: 500 * e^(-seconds/120)
  const timeScore = SCORING.TIME_BASE * Math.exp(-timeSeconds / SCORING.TIME_DECAY);
  
  // Move efficiency: 300 * (totalPieces / max(moves, totalPieces))
  const moveScore = SCORING.MOVE_BASE * (totalPieces / Math.max(moves, totalPieces));
  
  // Completion bonus
  const completionBonus = SCORING.COMPLETION_BONUS;
  
  // Perfect game bonus (exactly totalPieces moves)
  const isPerfect = moves === totalPieces;
  const perfectBonus = isPerfect ? DIFFICULTIES[difficulty].perfectBonus : 0;
  
  // Base score
  const baseScore = timeScore + moveScore + completionBonus + perfectBonus;
  
  // Apply difficulty multiplier
  const multiplier = DIFFICULTIES[difficulty].multiplier;
  const finalScore = baseScore * multiplier;
  
  return {
    score: Math.round(finalScore * 100) / 100,
    isPerfect,
    breakdown: {
      timeScore: Math.round(timeScore * 100) / 100,
      moveScore: Math.round(moveScore * 100) / 100,
      completionBonus,
      perfectBonus,
      multiplier
    }
  };
}

/**
 * Format time in MM:SS format
 */
export function formatTime(seconds) {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Get background position for a puzzle piece
 * @param {object} piece - The puzzle piece
 * @param {number} pieceSize - Size of each piece in pixels
 * @param {number} gridSize - Grid dimension (4 for mobile, 6 for desktop)
 */
export function getPieceBackgroundPosition(piece, pieceSize, gridSize = GRID_SIZE) {
  const x = -piece.correctCol * pieceSize;
  const y = -piece.correctRow * pieceSize;
  return `${x}px ${y}px`;
}

/**
 * Auto-correct rotation for hard mode
 */
export function autoCorrectRotation(piece) {
  return {
    ...piece,
    rotation: 0
  };
}
